import type { MedplumRequestOptions } from '@medplum/core'
import type {
  ExtendedResource,
  ExtendedResourceConstructor,
} from '@seen/core/ExtendedResource'
import {
  getSearchKey,
  normalizeQuery,
  type QueryTypes,
} from '@seen/core/search'
import {
  type UseQueryOptions,
  type UseQueryResult,
  useQueries,
} from '@tanstack/react-query'
import { toast } from 'sonner'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export interface ParallelSearchOneQuery<T extends ExtendedResource> {
  resourceClass: ExtendedResourceConstructor<T>
  query: QueryTypes | undefined
  options?: MedplumRequestOptions
}

export interface UseParallelSearchOneOptions<T extends ExtendedResource> {
  search?: MedplumRequestOptions
  query?: Omit<
    UseQueryOptions<T | null, Error, T | null>,
    'queryKey' | 'queryFn'
  >
}

/**
 * Hook for executing multiple search queries in parallel using React Query's useQueries
 * @param queries - Array of search queries to execute
 * @param options - Additional options for the search
 * @returns Array of query results in the same order as the input queries
 */
export function useParallelSearchOne<T extends ExtendedResource>(
  queries: ParallelSearchOneQuery<T>[],
  options: UseParallelSearchOneOptions<T> = {}
): UseQueryResult<T | null>[] {
  const medplum = useMedplum()

  const results = useQueries({
    queries: queries.map((q) => {
      const parameters = normalizeQuery(q.query)
      const searchKey = getSearchKey(
        medplum,
        q.resourceClass.RESOURCE_TYPE,
        parameters
      )

      return {
        ...options.query,
        // oxlint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey: cacheKeys.searchOneExtended(
          q.resourceClass.RESOURCE_TYPE,
          searchKey
        ),
        queryFn: async () =>
          medplum.searchOneResourceClass(q.resourceClass, q.query, {
            ...options.search,
            ...q.options,
          }),
        onError: (error: Error) => {
          toast.error(
            `Error executing search: ${error.message ?? 'Unknown error'}`
          )
        },
        meta: {
          ...options.query?.meta,
          queryParams: parameters,
          resourceClass: q.resourceClass,
        },
      }
    }),
  })

  return results
}
